import React from 'react';
import PropTypes from 'prop-types';
import { useForm, useField } from 'react-final-form';
import { makeStyles } from 'styles/util';
import { required } from 'form/validation';
import bookingStatuses from 'enums/bookingStatuses';
import additionalServiceStatuses from 'enums/additionalServiceStatuses';
import paymentRecurrences from 'enums/paymentRecurrences';
import bookingItemTypes from 'enums/bookingItemTypes';
import { getAdditionalServiceConfiguration } from 'helpers/AdditionalServiceHelper';
import { createTimePeriodPriceReferenceDictionary } from 'logic/timePeriodPriceLogic';

import Box from '@material-ui/core/Box';
import { TextField } from 'mui-rff';
import MenuItem from '@material-ui/core/MenuItem';
import { OnChange } from 'react-final-form-listeners';
import IconButton from '@material-ui/core/IconButton';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import TimePeriodPricesEditor from 'form/TimePeriodPricesEditor';

const useStyles = makeStyles(({ theme, colors }) => ({
    container: {
        border: `2px solid ${colors.lightGreyBorder}`,
        borderRadius: '6px',
        padding: theme.spacing(2, 2, 0, 2),
        marginBottom: theme.spacing(2),
        '& div, & label, & input, & p': {
            fontSize: '0.75rem'
        }
    },
    firstLineContainer: {
        display: 'flex',
        gap: theme.spacing(2)
    },
    additionalServiceContainer: {
        flex: '1 1 auto',
    },
    dataContainer: {
        flex: '1 1 20%',
    },
    removeButtonContainer: {
        flex: '0 0 auto',
        alignSelf: 'center',
        textAlign: 'end'
    },
    invisible: {
        visibility: 'hidden'
    }
}));

const BookingItemEditor = ({ fieldNamePrefix, booking, storageSite, itemCreator, onRemove }) => {
    const classes = useStyles();
    const form = useForm();
    const getName = suffix => suffix ? `${fieldNamePrefix}.${suffix}` : fieldNamePrefix;
    const item = useField(fieldNamePrefix).input.value;
    const selectedAdditionalService = storageSite.additionalServices.find(additionalService => additionalService.id === item.referenceId);
    const selectedAdditionalServiceConfiguration = getAdditionalServiceConfiguration(selectedAdditionalService, storageSite, true /* isAdmin */);

    const beforePurchase =
        booking.bookingStatus === bookingStatuses.requested.key ||
        booking.bookingStatus === bookingStatuses.confirmed.key;

    const disabled =
        !(
            booking.bookingStatus === bookingStatuses.requested.key ||
            booking.bookingStatus === bookingStatuses.confirmed.key ||
            item.paymentRecurrence === paymentRecurrences.perMonth.key ||
            (item.paymentRecurrence === paymentRecurrences.oneTime.key && !booking.subscriptionBooking) ||
            item.paymentRecurrence === undefined
        );

    const atLeastOneBookingItemTimePeriodPriceInUse = item.timePeriodPrices.some(tpp => tpp.inUse);

    const createListItem = additionalService => ({
        value: additionalService.id,
        title: additionalService.description,
        status: additionalService.status
    });

    const availableAdditionalServices = storageSite.additionalServices
        .filter(additionalService => !booking.subscriptionBooking || beforePurchase || additionalService.paymentRecurrence === paymentRecurrences.perMonth.key);

    const additionalServiceListItems = (disabled ? storageSite.additionalServices : availableAdditionalServices)
        .map(createListItem);

    const handleAdditionalServiceChange = newValue => {
        const newAdditionalService = storageSite.additionalServices.find(additionalService => additionalService.id === newValue);
        const newItem = itemCreator(newAdditionalService);
        ['referenceId', 'paymentRecurrence', 'data']
            .forEach(property => form.mutators.changeValue({ name: getName(property), value: newItem[property] }));
        newItem.timePeriodPrices.forEach((tpp, i) => {
            ['amount', 'vatRate', 'commissionRate', 'startDate', 'endDate']
                .forEach(property => form.mutators.changeValue({ name: getName(`timePeriodPrices[${i}].${property}`), value: newItem.timePeriodPrices[i][property] }));
        });
    };

    return (
        <Box className={classes.container}>
            <Box className={classes.firstLineContainer}>
                <Box className={classes.additionalServiceContainer}>
                    {
                        item.isPredefined &&
                        (
                            <>
                                <TextField
                                    select
                                    size="small"
                                    name={getName('referenceId')}
                                    label="Additional service"
                                    variant="outlined"
                                    disabled={disabled || atLeastOneBookingItemTimePeriodPriceInUse}
                                >
                                    {
                                        additionalServiceListItems
                                            .map(additionalService => (
                                                <MenuItem key={additionalService.value} value={additionalService.value}>
                                                    {additionalService.title}
                                                    {
                                                        additionalService.status === additionalServiceStatuses.deleted.key && ' - deleted'
                                                    }
                                                </MenuItem>
                                            ))
                                    }
                                </TextField>
                                <OnChange name={getName('referenceId')}>
                                    {handleAdditionalServiceChange}
                                </OnChange>
                            </>
                        )
                    }
                    {
                        !item.isPredefined &&
                        (
                            <TextField
                                name={getName('description')}
                                size="small"
                                label="Additional service"
                                variant="outlined"
                                disabled={disabled}
                                required
                                fieldProps={{ validate: required }}
                                placeholder="Enter service description"
                            />
                        )
                    }
                </Box>
                <Box className={classes.dataContainer}>
                    <TextField
                        name={getName('data')}
                        size="small"
                        variant="outlined"
                        className={selectedAdditionalServiceConfiguration?.showDataTextbox ? undefined : classes.invisible}
                        disabled={disabled}
                        {...selectedAdditionalServiceConfiguration?.dataTextboxProps}
                        helperText={undefined}
                    />
                </Box>
                {
                    !disabled && (item.id === undefined || beforePurchase) &&
                    (
                        <Box className={classes.removeButtonContainer}>
                            <IconButton
                                onClick={onRemove}
                            >
                                <DeleteOutlineOutlinedIcon/>
                            </IconButton>
                        </Box>
                    )
                }
            </Box>

            <TimePeriodPricesEditor
                fieldNamePrefix={getName('timePeriodPrices')}
                amountLabel={paymentRecurrences[item.paymentRecurrence].title}
                currency={booking.currency}
                referenceDictionary={createTimePeriodPriceReferenceDictionary(storageSite)}
                validateFullCoverage={item.type === bookingItemTypes.storage.key}
                vatRateVisible
                commissionRateVisible
                timePeriodVisible={item.paymentRecurrence === paymentRecurrences.perMonth.key}
                linkIconVisible
            />
        </Box>
    );
};

BookingItemEditor.propTypes = {
    fieldNamePrefix: PropTypes.string.isRequired,
    booking: PropTypes.object.isRequired,
    storageSite: PropTypes.object.isRequired,
    itemCreator: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired
};

export default BookingItemEditor;
